import { useEffect, useMemo, useState } from "react";
import { api, apiErrToStr, fmtDate } from "@/lib/api";
import { Clock3, RefreshCw, Save } from "lucide-react";

const TIMEZONES = [
  "America/Sao_Paulo",
  "America/Manaus",
  "America/Belem",
  "America/Fortaleza",
  "America/Recife",
  "America/Rio_Branco",
  "UTC",
];

const DEFAULT_STATE = {
  timezone: "America/Sao_Paulo",
  ntp_enabled: true,
  ntp_synchronized: false,
  local_rtc: false,
  server_time_iso: "",
  server_time_label: "",
  timezones: ["America/Sao_Paulo"],
  updated_at: "",
  updated_by: "",
};

function toDateTimeLocal(value) {
  if (!value) return "";
  try {
    const date = new Date(value);
    const pad = (n) => String(n).padStart(2, "0");
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  } catch (_err) {
    return "";
  }
}

export default function SystemDateTime() {
  const [state, setState] = useState(DEFAULT_STATE);
  const [manualDateTime, setManualDateTime] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const [filter, setFilter] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/system/time-settings");
      setState({
        ...DEFAULT_STATE,
        timezone: data.timezone || "America/Sao_Paulo",
        ntp_enabled: true,
        ntp_synchronized: true,
        local_rtc: false,
        server_time_iso: data.local_time || "",
        server_time_label: data.local_time ? new Date(data.local_time).toLocaleString("pt-BR") : "",
        timezones: TIMEZONES,
        updated_at: data.updated_at || "",
        updated_by: data.updated_by || "",
      });
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filteredTimezones = useMemo(() => {
    const term = String(filter || "").trim().toLowerCase();
    const rows = state.timezones || [];
    if (!term) return rows;
    return rows.filter((item) => String(item).toLowerCase().includes(term));
  }, [filter, state.timezones]);

  const save = async () => {
    setSaving(true);
    setError("");
    setMessage("");
    try {
      const payload = {
        timezone: state.timezone,
      };
      const { data } = await api.put("/system/time-settings", payload);
      setState((current) => ({
        ...current,
        timezone: data.timezone || current.timezone,
        server_time_iso: data.local_time || current.server_time_iso,
        server_time_label: data.local_time ? new Date(data.local_time).toLocaleString("pt-BR") : current.server_time_label,
        updated_at: data.updated_at || current.updated_at,
        updated_by: data.updated_by || current.updated_by,
      }));
      setManualDateTime("");
      setMessage("Data e hora do sistema atualizadas.");
      window.dispatchEvent(new Event("voxyra-ipbx-config-pending"));
    } catch (err) {
      setError(apiErrToStr(err?.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="system-datetime-page">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Configuracao do host</div>
          <h1 className="text-2xl font-semibold tracking-tight">Data e hora</h1>
          <p className="text-sm text-[color:var(--text-secondary)] mt-1">Ajuste o fuso horario, o NTP e a hora manual do servidor do Voxyra IPBX.</p>
        </div>
        <div className="flex items-center gap-2">
          <button className="sbc-btn" onClick={load} disabled={loading}>
            <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Atualizar
          </button>
          <button className="sbc-btn sbc-btn-primary" onClick={save} disabled={saving}>
            <Save size={14} className={saving ? "animate-pulse" : ""} /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>

      {error && <div className="text-xs font-mono text-[color:var(--accent-red)] border border-[rgba(239,68,68,0.25)] bg-[rgba(239,68,68,0.08)] px-3 py-2 rounded-sm">! {error}</div>}
      {message && <div className="text-xs font-mono text-[color:var(--accent-green)] border border-[rgba(16,185,129,0.25)] bg-[rgba(16,185,129,0.08)] px-3 py-2 rounded-sm">{message}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Hora atual</div>
          <div className="text-2xl font-mono mt-2">{state.server_time_label || "--:--:--"}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">{state.timezone}</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">NTP</div>
          <div className="text-2xl font-mono mt-2">ATIVO</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Sincronismo administrado pelo host</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">RTC local</div>
          <div className="text-2xl font-mono mt-2">HOST</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">Informacao resumida do servidor</div>
        </div>
        <div className="sbc-card p-4">
          <div className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)]">Ultima alteracao</div>
          <div className="text-sm font-mono mt-2">{fmtDate(state.updated_at)}</div>
          <div className="text-xs text-[color:var(--text-muted)] mt-1">{state.updated_by || "-"}</div>
        </div>
      </div>

      <div className="sbc-card p-4 space-y-4">
        <div className="grid grid-cols-1 lg:grid-cols-[260px_minmax(0,1fr)] gap-4">
          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block">Sincronizacao automatica</label>
            <label className="inline-flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked
                disabled
              />
              NTP ativo no servidor
            </label>
            <div className="text-xs text-[color:var(--text-muted)]">
              Nesta tela voce ajusta o fuso horario. O sincronismo fino da hora continua sob controle do host.
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] block">Filtrar fusos</label>
            <input className="sbc-input" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Ex.: Sao_Paulo, UTC, Lisbon" />
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <label className="space-y-1 text-xs">
            <span>Fuso horario</span>
            <select className="sbc-input w-full" value={state.timezone} onChange={(e) => setState((current) => ({ ...current, timezone: e.target.value }))}>
              {filteredTimezones.map((timezone) => <option key={timezone} value={timezone}>{timezone}</option>)}
            </select>
          </label>
          <label className="space-y-1 text-xs">
            <span>Data e hora manual</span>
            <input
              className="sbc-input w-full"
              type="datetime-local"
              value={manualDateTime}
              onChange={(e) => setManualDateTime(e.target.value)}
              disabled
              placeholder={toDateTimeLocal(state.server_time_iso)}
            />
          </label>
        </div>

        <div className="text-xs text-[color:var(--text-muted)] flex items-center gap-2">
          <Clock3 size={14} />
          A hora manual fica indisponivel enquanto o backend expuser apenas o ajuste de fuso horario.
        </div>
      </div>
    </div>
  );
}
